import additions from "./additions.json";
import customer from "./themes/customer.json";
import guide from "./tokens.json";

// Resolves the design tokens (the guide's tokens.json, additions.json and the app
// themes) into per-theme values and --ax-* CSS variables. The Tailwind preset, the
// generated tokens.css and JS consumers all read from here.

type Themed = string | Record<string, string>;
type Token = { name: string; value: Themed };
type TypeProps = {
  fontSize: string;
  lineHeight: string;
  fontWeight: number | string;
  letterSpacing?: string;
};
export type TypeStyle = TypeProps & { name: string };
export type Theme = {
  id: string;
  basedOn?: string;
  values?: Record<string, string>;
};

export const themes: Theme[] = [...guide.color.themes, customer];
const guideThemes = guide.color.themes.map((theme) => theme.id);

export const colorTokens: Token[] = [
  ...guide.color.tokens,
  ...additions.color.tokens,
];
// Families whose values can differ per theme; each token becomes an --ax-<name> variable.
const themedTokens: Token[] = [
  ...colorTokens,
  ...guide.radius.tokens,
  ...guide.shadow.tokens,
  ...guide.size.tokens,
];
const tokensByName = new Map(themedTokens.map((token) => [token.name, token]));
export const typeStyles: TypeStyle[] = [
  ...guide.type.groups,
  ...additions.type.groups,
].flatMap((group) => group.styles);

const themeById = (id: string) => {
  const theme = themes.find((candidate) => candidate.id === id);
  if (!theme) throw new Error(`Unknown theme "${id}"`);
  return theme;
};

// A theme's own value wins; otherwise the guide theme it builds on. `{name}` aliases another token.
const resolve = (token: Token, theme: Theme): string => {
  const own = theme.values?.[token.name];
  if (own) return own;
  const guideTheme = theme.basedOn ?? theme.id;
  const value =
    typeof token.value === "string"
      ? token.value
      : token.value[guideTheme] ?? token.value[guideThemes[0]];
  const alias = /^\{(.+)\}$/.exec(value)?.[1];
  const target = alias && tokensByName.get(alias);
  return target ? resolve(target, theme) : value;
};

export const tokenValue = (name: string, themeId = themes[0].id) => {
  const token = tokensByName.get(name);
  if (!token) throw new Error(`Unknown token "${name}"`);
  return resolve(token, themeById(themeId));
};

export const cssVar = (name: string) => `--ax-${name}`;

const TYPE_VARS = {
  fontSize: "size",
  lineHeight: "line",
  fontWeight: "weight",
  letterSpacing: "tracking",
} as const;
export const typeVar = (style: string, prop: keyof TypeProps) =>
  `--ax-text-${style}-${TYPE_VARS[prop]}`;

// Letter spacing is only set for styles that define it, so other styles keep inherited tracking.
export const isTracked = (style: TypeStyle) => Boolean(style.letterSpacing);

// Opaque hex colors are stored as RGB channels so opacity modifiers (`bg-ink/50`) work.
export const toChannels = (color: string) => {
  const hex = /^#([0-9a-f]{6})$/i.exec(color)?.[1];
  if (!hex) return undefined;
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(" ");
};

const themeVariables = (theme: Theme) => {
  const variables: Record<string, string> = {};
  for (const token of themedTokens) {
    const value = resolve(token, theme);
    variables[cssVar(token.name)] = toChannels(value) ?? value;
  }
  for (const style of typeStyles) {
    for (const prop of Object.keys(TYPE_VARS) as (keyof TypeProps)[]) {
      if (prop === "letterSpacing" && !isTracked(style)) continue;
      variables[typeVar(style.name, prop)] = String(style[prop]);
    }
  }
  return variables;
};

// The first theme also applies on :root, so pages without `data-theme` get it.
export const themeStyles = () =>
  Object.fromEntries(
    themes.map((theme, i) => [
      i === 0
        ? `:root, [data-theme="${theme.id}"]`
        : `[data-theme="${theme.id}"]`,
      themeVariables(theme),
    ])
  );

export const themeCss = () =>
  Object.entries(themeStyles())
    .map(
      ([selector, variables]) =>
        `${selector} {\n${Object.entries(variables)
          .map(([name, value]) => `  ${name}: ${value};`)
          .join("\n")}\n}`
    )
    .join("\n\n") + "\n";
